import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Storefront } from '@phosphor-icons/react';
import ImageInputField from '@/components/admin/ImageInputField';

const EMPTY = {
  shop_name: '', owner_name: '', email: '', mobile: '', password: '',
  pincode: '', address: '',
  bank_account_number: '', account_holder_name: '', ifsc: '', gstin: '',
  upi_id: '',
  shop_photo_url: '', kyc_doc_url: '', upi_qr_url: '',
  terms_accepted: false,
};

export default function SellerRegister() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) { toast.error('Please accept the terms & conditions'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/sellers/register', form);
      localStorage.setItem('zk_token', data.token);
      if (setUser) setUser(data.user);
      toast.success('Account created! Awaiting admin approval.');
      navigate('/seller/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not register');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 bg-grain py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-stone-200 p-7 shadow-sm" data-testid="seller-register-page">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-10 h-10 rounded-2xl bg-olive-700 text-white grid place-items-center"><Storefront size={22}/></span>
          <div>
            <p className="font-heading text-xl text-stone-900">Open a Store on MyZippyKart</p>
            <p className="text-xs text-stone-500">Reach local customers in your PIN code · 5% commission</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <Section title="Store & owner">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Shop name" value={form.shop_name} onChange={(v) => upd('shop_name', v)} testId="seller-shop-name" required/>
              <Input label="Your name" value={form.owner_name} onChange={(v) => upd('owner_name', v)} testId="seller-owner-name" required/>
              <Input label="Email" type="email" value={form.email} onChange={(v) => upd('email', v)} testId="seller-email" required/>
              <Input label="Mobile" value={form.mobile} onChange={(v) => upd('mobile', v.replace(/[^0-9]/g, '').slice(0, 12))} testId="seller-mobile" required/>
              <Input label="Password" type="password" value={form.password} onChange={(v) => upd('password', v)} testId="seller-password" required/>
              <Input label="PIN code" value={form.pincode} onChange={(v) => upd('pincode', v.replace(/[^0-9]/g, '').slice(0, 6))} testId="seller-pincode" required/>
            </div>
            <Input label="Shop address" value={form.address} onChange={(v) => upd('address', v)} testId="seller-address"/>
          </Section>

          <Section title="Bank & payout details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Account holder name" value={form.account_holder_name} onChange={(v) => upd('account_holder_name', v)} testId="seller-acc-holder"/>
              <Input label="Bank account number" value={form.bank_account_number} onChange={(v) => upd('bank_account_number', v.replace(/[^0-9]/g, '').slice(0, 25))} testId="seller-acc-number"/>
              <Input label="IFSC code" value={form.ifsc} onChange={(v) => upd('ifsc', v.toUpperCase().slice(0, 15))} testId="seller-ifsc"/>
              <Input label="GSTIN (optional)" value={form.gstin} onChange={(v) => upd('gstin', v.toUpperCase().slice(0, 15))} testId="seller-gstin"/>
              <Input label="UPI ID" value={form.upi_id} onChange={(v) => upd('upi_id', v.trim().slice(0, 80))} testId="seller-upi-id" placeholder="yourname@okaxis"/>
            </div>
          </Section>

          <Section title="KYC & shop proof">
            <ImageInputField label="Shop photo" value={form.shop_photo_url} onChange={(v) => upd('shop_photo_url', v)} testIdInput="seller-shop-photo-url" testIdUploadBtn="seller-shop-photo-upload" folder="kyc/"/>
            <ImageInputField label="KYC document (Aadhaar / PAN / License)" value={form.kyc_doc_url} onChange={(v) => upd('kyc_doc_url', v)} testIdInput="seller-kyc-doc-url" testIdUploadBtn="seller-kyc-doc-upload" folder="kyc/"/>
            <ImageInputField label="UPI QR scanner (optional)" value={form.upi_qr_url} onChange={(v) => upd('upi_qr_url', v)} testIdInput="seller-upi-qr-url" testIdUploadBtn="seller-upi-qr-upload" folder="kyc/"/>
            <p className="text-[11px] text-stone-500">Your bank/KYC details are visible only to platform administrators.</p>
          </Section>

          <div className="bg-cream-50 border border-stone-200 rounded-xl p-3">
            <label className="flex gap-2 text-xs text-stone-700">
              <input
                data-testid="seller-terms-accept"
                type="checkbox"
                checked={!!form.terms_accepted}
                onChange={(e) => upd('terms_accepted', e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I accept the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-olive-800 font-semibold underline">MyZippyKart Vendor Terms &amp; Conditions</a>, including: platform commission on sales, admin may reject or suspend my account for policy violations, and daily rejection of more than 3 customer orders may auto-suspend my account for 2 days.
              </span>
            </label>
          </div>

          <button data-testid="seller-register-submit" disabled={busy || !form.terms_accepted} type="submit" className="w-full h-12 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">
            {busy ? 'Creating…' : 'Create store account'}
          </button>
        </form>

        <p className="text-xs text-stone-500 mt-5">
          Already have a seller account? <Link to="/seller/login" className="text-olive-800 font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="font-heading text-sm text-stone-800 uppercase tracking-wide">{title}</h3>
      {children}
    </div>
  );
}

function Input({ label, type = 'text', value, onChange, testId, required, placeholder }) {
  return (
    <div>
      <label className="text-xs font-medium text-stone-600">{label}{required && ' *'}</label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full h-11 px-4 mt-1 rounded-xl bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm"/>
    </div>
  );
}
